import { useTranslation } from 'react-i18next';
import { Page } from '@/components/layout/Page';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageContent } from '@/components/layout/PageContent';

export function PrivacyPolicyPage() {
    const { t } = useTranslation('legal');

    return (
        <Page className="bg-slate-50">
            <PageHeader title={t('privacy.title')} backLink="/" width="narrow" />

            <PageContent width="narrow">
                <div className="space-y-6 pb-8">

                    {/* Intro */}
                    <Section title={t('privacy.intro.title')}>
                        <p>{t('privacy.intro.content')}</p>
                    </Section>

                    {/* Controller */}
                    <Section title={t('privacy.controller.title')}>
                        <p>{t('privacy.controller.content')}</p>
                    </Section>

                    {/* Data Collected */}
                    <Section title={t('privacy.collected.title')}>
                        <p className="font-medium text-slate-700">{t('privacy.collected.subtitle1')}</p>
                        <ul className="list-disc pl-5 space-y-1 mt-1">
                            {([1, 2, 3, 4, 5] as const).map((n) => (
                                <li key={n}>{t(`privacy.collected.item${n}`)}</li>
                            ))}
                        </ul>

                        <p className="font-medium text-slate-700 mt-3">{t('privacy.collected.subtitle2')}</p>
                        <ul className="list-disc pl-5 space-y-1 mt-1">
                            {([6, 7] as const).map((n) => (
                                <li key={n}>{t(`privacy.collected.item${n}`)}</li>
                            ))}
                        </ul>

                        <p className="font-medium text-slate-700 mt-3">{t('privacy.collected.subtitle3')}</p>
                        <ul className="list-disc pl-5 space-y-1 mt-1">
                            {([8, 9, 10] as const).map((n) => (
                                <li key={n}>{t(`privacy.collected.item${n}`)}</li>
                            ))}
                        </ul>
                    </Section>

                    {/* Purposes */}
                    <Section title={t('privacy.purposes.title')}>
                        <p>{t('privacy.purposes.content')}</p>
                        <ul className="list-disc pl-5 space-y-1 mt-2">
                            {([1, 2, 3, 4, 5, 6] as const).map((n) => (
                                <li key={n}>{t(`privacy.purposes.item${n}`)}</li>
                            ))}
                        </ul>
                    </Section>

                    {/* Legal Basis */}
                    <Section title={t('privacy.legal.title')}>
                        <ul className="list-disc pl-5 space-y-2">
                            {([1, 2, 3] as const).map((n) => (
                                <li key={n}>{t(`privacy.legal.item${n}`)}</li>
                            ))}
                        </ul>
                    </Section>

                    {/* Retention */}
                    <Section title={t('privacy.retention.title')}>
                        <p>{t('privacy.retention.content')}</p>
                    </Section>

                    {/* Recipients */}
                    <Section title={t('privacy.recipients.title')}>
                        <p>{t('privacy.recipients.content')}</p>
                        <ul className="list-disc pl-5 space-y-1 mt-2">
                            {([1, 2, 3, 4, 5, 6, 7] as const).map((n) => (
                                <li key={n}>{t(`privacy.recipients.item${n}`)}</li>
                            ))}
                        </ul>
                        <p className="mt-3 text-xs text-slate-500">{t('privacy.recipients.transfer')}</p>
                    </Section>

                    {/* Rights */}
                    <Section title={t('privacy.rights.title')}>
                        <p>{t('privacy.rights.content')}</p>
                        <ul className="list-disc pl-5 space-y-1 mt-2">
                            {([1, 2, 3, 4, 5, 6] as const).map((n) => (
                                <li key={n}>{t(`privacy.rights.item${n}`)}</li>
                            ))}
                        </ul>
                        <p className="mt-3">{t('privacy.rights.how')}</p>
                        <p className="mt-2 text-xs text-slate-500">{t('privacy.rights.cnil')}</p>
                    </Section>

                    {/* Cookies */}
                    <Section title={t('privacy.cookies.title')}>
                        <p>{t('privacy.cookies.content')}</p>
                        <ul className="list-disc pl-5 space-y-1 mt-2">
                            {([1, 2, 3] as const).map((n) => (
                                <li key={n}>{t(`privacy.cookies.item${n}`)}</li>
                            ))}
                        </ul>
                        <p className="mt-3 bg-emerald-50 text-emerald-700 text-xs rounded-xl px-3 py-2">{t('privacy.cookies.notice')}</p>
                    </Section>

                    {/* Security */}
                    <Section title={t('privacy.security.title')}>
                        <p>{t('privacy.security.content')}</p>
                    </Section>

                    {/* Minors */}
                    <Section title={t('privacy.minors.title')}>
                        <p>{t('privacy.minors.content')}</p>
                    </Section>

                    {/* Modifications */}
                    <Section title={t('privacy.modifications.title')}>
                        <p>{t('privacy.modifications.content')}</p>
                    </Section>

                    {/* Contact */}
                    <Section title={t('privacy.contact.title')}>
                        <p>{t('privacy.contact.content')}</p>
                    </Section>

                    <p className="text-xs text-slate-400 text-center pt-2">{t('privacy.lastUpdate')}</p>
                </div>
            </PageContent>
        </Page>
    );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 space-y-3">
            <h2 className="text-sm font-bold text-slate-900">{title}</h2>
            <div className="text-sm text-slate-600 leading-relaxed">{children}</div>
        </div>
    );
}
